import { memo, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import HotelTopBanner from '../HeroBanner/HotelTopBanner';
import ContentWrapper from '../../common/ContentWrapper';
import MiniSiteTabs from './MiniSiteTabs';
import Access from '../Access';
import { MiniSiteTabOptions, PaymentMethod } from '../../../core/constants';
import type { Hotel } from '../../../core/isValidHotel';
import type MiniSiteContents from '../../../../types/MiniSiteContents';
import type { StartPrices } from '../../../../types/StartPrices';
import { filterFaqsByLocale } from '../../../core/helpers/faq';
import FacilitiesAndRoom from '../FacilitiesAndRoom';
import CheckoutIn from '../CheckoutIn';
import Policy from '../Policy';
import FAQs from '../../faq/FAQs';
import MiniSiteHotelDetails from './MiniSiteHotelDetails/MiniSiteHotelDetails';
import ReviewAndAccess from '../ReviewAndAccess/ReviewAndAccess';

const Alert = dynamic(() => import('../../common/Alert'));
const Link = dynamic(() => import('../../common/Link/Link'));

type Props = {
  hotel: Hotel;
  miniSiteContents: MiniSiteContents;
  preview?: boolean | null;
  setIsModalOpen: (bool: boolean) => void;
  roomsTabContent: JSX.Element;
  startPrices?: StartPrices;
  tabistPaymentTypes?: PaymentMethod[];
  isStartPricesLoading: boolean;
};

const MiniSite = ({
  hotel,
  miniSiteContents,
  preview,
  setIsModalOpen,
  roomsTabContent,
  startPrices,
  tabistPaymentTypes,
  isStartPricesLoading,
}: Props) => {
  const router = useRouter();
  const storage = typeof window !== 'undefined' ? window?.sessionStorage : undefined;
  const previousPath = storage?.getItem('prevPath');
  const { t } = useTranslation('hotel');
  const accessDivRef = useRef<HTMLDivElement>(null);
  const scrollToAccess = () => {
    accessDivRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const currentTab =
    router.pathname !== '/b/[hotel]/configure' &&
    previousPath?.match(`/b/${hotel.id}/configure`) &&
    !router.query.tab // if user comes back from configure page, navigates to rooms tab
      ? MiniSiteTabOptions.ROOMS
      : (router.query.tab as MiniSiteTabOptions) ||
        (router.pathname !== '/b/[hotel]/configure'
          ? MiniSiteTabOptions.HOTEL_DETAILS // hotel details tab is default in hotel details page
          : MiniSiteTabOptions.ROOMS); // rooms tab is default in configure page

  const faqs = filterFaqsByLocale(hotel?.faqs, router.locale);

  const renderContents = () => {
    const hasSomePolicy =
      !!hotel?.allowPayAtHotel ||
      !!hotel?.allowPrepay ||
      !!hotel?.payment ||
      !!hotel?.tabistPaymentTypes ||
      !!hotel?.propertyPaymentTypes ||
      !!hotel?.allowPayAtHotelForForeignGuests ||
      !!hotel?.creditcard ||
      !!hotel?.precautions ||
      !!hotel?.noShowPolicy;

    if (currentTab === MiniSiteTabOptions.HOTEL_DETAILS)
      return (
        <>
          <MiniSiteHotelDetails hotel={hotel} miniSiteContents={miniSiteContents} />
          <div className="bg-white">
            <ContentWrapper className="pb-12 pt-10 sm:pb-28">
              <FacilitiesAndRoom hotel={hotel} />
              {!!hotel?.checkinCheckout && <CheckoutIn title={hotel.checkinCheckout} />}
              {hasSomePolicy && (
                <div className="my-10 sm:my-20">
                  <Policy
                    cancellationPolicy={t('hotel:policy.cancel_policy_description')}
                    tabistPaymentTypes={tabistPaymentTypes}
                    propertyPaymentTypes={hotel?.propertyPaymentTypes}
                    allowPayAtHotelForForeignGuests={hotel?.allowPayAtHotelForForeignGuests}
                    creditcard={hotel?.creditcard}
                    precautions={hotel?.precautions}
                    noShowPolicy={hotel?.noShowPolicy}
                  />
                </div>
              )}
              <Access hotel={hotel} ref={accessDivRef} />
              {faqs.length > 0 && (
                <div className="mt-10 sm:mt-20">
                  <FAQs faqs={faqs} hotelId={hotel?.id} />
                </div>
              )}
            </ContentWrapper>
          </div>
        </>
      );

    return roomsTabContent;
  };

  const MiniSiteHeader = useMemo(
    () => (
      <ContentWrapper className="mb-4 sm:space-y-6 sm:px-0 lg:px-8 lg:pt-6 sm-max:px-0">
        <div className="flex flex-col-reverse gap-6 sm:flex-col">
          <HotelTopBanner hotel={hotel} setIsModalOpen={setIsModalOpen} />
          <div className="space-y-2 p-4 pb-2 sm:pb-0 lg:p-0">
            <h1 className="text-2xl font-bold sm:text-3xl">{hotel.name}</h1>
            <ReviewAndAccess hotel={hotel} scrollToAccess={scrollToAccess} />
          </div>
        </div>
      </ContentWrapper>
    ),
    [hotel, setIsModalOpen]
  );

  return (
    <main>
      {preview && (
        <Alert severity="info" className="flex justify-center align-middle" icon={false}>
          <>
            <p className="font-bold">You are on Preview mode</p>
            <span className="block sm:ml-2 sm:inline-block">
              <Link href="/api/exit-preview">
                <a target="_self">
                  <p className="cursor-pointer underline">Exit Preview Mode</p>
                </a>
              </Link>
            </span>
          </>
        </Alert>
      )}
      {MiniSiteHeader}
      <MiniSiteTabs
        currentTab={currentTab}
        startPrices={startPrices}
        isStartPricesLoading={isStartPricesLoading}
        setIsModalOpen={setIsModalOpen}
        hotel={hotel}
      />
      {/* This is a spacer to show half bg gray color */}
      <div className="-mt-8 h-8 bg-gray-50" />

      {renderContents()}
    </main>
  );
};

export default memo(MiniSite);
